import {ArrowLeftIcon, XIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {Dialog, FeatureFlags} from '@primer/react/experimental'
import {memo, Suspense, useEffect, useCallback, useMemo, useRef, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLocation} from '@github-ui/react-router'
import {
  type CommentsFilterState,
  getDefaultFilterState,
  useFilteredThreadIds,
  useFilterIssueCommentsIds,
} from './comments-panel/CommentsFilter'
import type {PageLimits} from '../../page-data/payloads/files'
import {
  useThreads,
  useIssueComments,
  useReviewSummaries,
  useDiffPathOrderMap,
  usePullRequestBasePath,
} from '../../stores/selectors'
import {
  useCommentReplyActions,
  useCommentsPanelViewMode,
  useLayoutCanComment,
  useLayoutStoreSeeded,
  useSelectedCommentReplyThreadId,
} from '../../stores/layout-selectors'
import {reviewSummaryToIssueComment, buildConversationItems} from './review-summary-to-issue-comment'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelMetadata,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {createPortal} from 'react-dom'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import type {AliveChannels} from '../../page-data/payloads/header'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './DockedSidePanel'
import {Panel} from './Panel'
import {noop} from '@github-ui/noop'
import {CommentPanelHeaderContent, isReviewCommentsViewMode} from './comments-panel/CommentPanelHeaderContent'
import {CommentsPanelBody} from './comments-panel/CommentsPanelBody'
import {CommentsSidePanelFooter, type CommentsSidePanelFooterHandle} from './comments-panel/CommentsSidePanelFooter'
import {
  sortThreadPreviewsByDiffOrder,
  buildThreadPreviewsAuthorList,
  buildIssueCommentsAuthorList,
} from '../../helpers/comments-panel-helper'
import {useFileTreeDiffSummaries} from '../../page-data/loaders/use-file-tree-diff-summaries'
import {useInfiniteThreadsPageData} from '../../page-data/loaders/use-threads'
import {useActiveRoute} from '../../hooks/use-active-route'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {produce} from 'immer'

interface CommentsSidePanelProps {
  onClose: () => void
  pageLimits?: PageLimits
  pathName: string
  pullRequestAuthorLogin: string
  aliveChannels: AliveChannels
  isOpen: boolean
  tabSize?: number
  filteredThreadIds: Set<string>
}

/**
 * Renders nothing but lazily fetches file tree and thread data into the store
 * when rendered. Fetches the file tree (non-suspending) and all thread pages
 * (suspending). Must be wrapped in a Suspense boundary for the thread fetch.
 */
function LazyDataLoader({onLoaded}: {onLoaded: () => void}) {
  const basePath = usePullRequestBasePath()

  const {isFetched: isFileTreeFetched} = useFileTreeDiffSummaries({basePath, enabled: true})

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetched: areThreadsFetched,
  } = useInfiniteThreadsPageData({
    pathName: basePath,
    hasMorePages: true,
  })

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (isFileTreeFetched && areThreadsFetched) {
      onLoaded()
    }
  }, [isFileTreeFetched, areThreadsFetched, onLoaded])

  return null
}

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments
 */
export const CommentsSidePanel = memo(function CommentsSidePanel(
  props: {
    toggleSidesheetRef: React.RefObject<HTMLElement | null>
  } & Omit<CommentsSidePanelProps, 'setThreadPreviews' | 'filteredThreadIds'>,
) {
  const {pathName, toggleSidesheetRef, pageLimits, pullRequestAuthorLogin, aliveChannels, isOpen, onClose, tabSize} =
    props

  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Comments)
  // Get portal target - only accessed when panel is open, so SSR-safe
  const sidePanelContent = isDockedPanelOpen ? ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID) : null

  const viewMode = useCommentsPanelViewMode()
  const selectedThreadId = useSelectedCommentReplyThreadId()
  const {clearReplyDetail} = useCommentReplyActions()
  const unsortedThreadPreviews = useThreads()
  const totalDiffCommentCount = unsortedThreadPreviews.length
  const diffPathOrderMap = useDiffPathOrderMap()
  const activeRoute = useActiveRoute()
  const isFilesChanged = activeRoute === 'files_changed'

  const threadPreviews = useMemo(
    () => sortThreadPreviewsByDiffOrder(unsortedThreadPreviews, diffPathOrderMap),
    [unsortedThreadPreviews, diffPathOrderMap],
  )
  const issueComments = useIssueComments()
  const reviewSummaries = useReviewSummaries()
  const currentUserCanComment = useLayoutCanComment()

  // Build a chronologically sorted list of conversation items (issue comments + review summaries)
  // preserving original types so each renders with its dedicated component
  const conversationItems = useMemo(
    () => buildConversationItems(issueComments, reviewSummaries),
    [issueComments, reviewSummaries],
  )

  // Flatten to IssueComment shape for filtering (useFilterIssueCommentsIds expects IssueComment[])
  const mergedConversationComments = useMemo(() => {
    const convertedSummaries = reviewSummaries.map(reviewSummaryToIssueComment)
    return [...issueComments, ...convertedSummaries]
  }, [issueComments, reviewSummaries])

  const [threadPreviewFilterState, setThreadPreviewFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )

  // Open panel and apply filter from URL review_id param
  const {openPanel, closePanel: closePanelAction} = useDockedPanelActions()
  const location = useLocation()
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const reviewId = urlParams.get('review_id')

    if (!reviewId) return
    const showResolved = urlParams.get('show_resolved')
    const resolvedFilter = showResolved === 'true' ? ' is:resolved' : showResolved === 'false' ? ' is:unresolved' : ''
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThreadPreviewFilterState(prev => ({
      ...prev,
      filterText: `review-id:${reviewId}${resolvedFilter}`.trim(),
    }))
    openPanel(DockedPanel.Comments)

    return () => {
      closePanelAction()
    }
  }, [location.pathname, location.search, openPanel, closePanelAction])

  // Apply panel metadata (e.g. author filter from action items) during render to avoid a
  // double-render from an effect. Tracks the last applied metadata object so the update
  // only fires when metadata changes while the panel is open.
  const panelMetadata = useDockedPanelMetadata()
  const [lastAppliedMetadata, setLastAppliedMetadata] = useState(panelMetadata)
  if (isOpen && panelMetadata && panelMetadata !== lastAppliedMetadata) {
    setLastAppliedMetadata(panelMetadata)
    if (panelMetadata.selectedAuthor || panelMetadata.hideResolved !== undefined) {
      setThreadPreviewFilterState(prev =>
        produce(prev, draft => {
          if (panelMetadata.selectedAuthor) draft.selectedAuthor = panelMetadata.selectedAuthor
          if (panelMetadata.hideResolved !== undefined) draft.hideResolvedThreads = panelMetadata.hideResolved
        }),
      )
    }
  }

  const filteredThreadIds = useFilteredThreadIds(threadPreviews, threadPreviewFilterState)
  const [issueCommentsFilterState, setIssueCommentsFilterState] = useState<CommentsFilterState>(() =>
    getDefaultFilterState(),
  )
  const filteredIssueCommentIds = useFilterIssueCommentsIds(mergedConversationComments, issueCommentsFilterState)
  const selectedThreadIdInView =
    selectedThreadId && filteredThreadIds.has(selectedThreadId) ? selectedThreadId : undefined
  const isThreadDetailMode = isReviewCommentsViewMode(viewMode) && selectedThreadIdInView !== undefined

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const footerRef = useRef<CommentsSidePanelFooterHandle>(null)

  // State for comment footer
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending: isSubmittingComment} = useCreatePullRequestTimelineCommentMutation()

  const handleCommentTextChange = useCallback((value: string) => {
    setCommentText(value)
  }, [])

  const handleSubmitComment = useCallback(() => {
    if (commentText.trim().length === 0 || isSubmittingComment) return
    createTimelineComment(
      {text: commentText},
      {
        onSuccess: data => {
          setCommentText('')
          requestAnimationFrame(() => {
            ssrSafeDocument
              ?.getElementById(`issue-comment-${data.issueComment.id}`)
              ?.scrollIntoView({behavior: 'smooth'})
          })
        },
      },
    )
  }, [commentText, createTimelineComment, isSubmittingComment])

  const handleQuoteReply = useCallback((quotedText: string) => {
    const wasMinimized = footerRef.current?.expand() ?? false
    if (wasMinimized) {
      setCommentText(prev => {
        if (prev.trim().length > 0) {
          return `${prev}\n\n${quotedText}`
        }
        return quotedText
      })
    } else {
      setCommentText(quotedText)
    }
  }, [])
  const handleBackToThreadList = useCallback(() => {
    clearReplyDetail()
  }, [clearReplyDetail])

  const threadPreviewsAuthorList = useMemo(() => buildThreadPreviewsAuthorList(threadPreviews), [threadPreviews])

  const issueCommentsAuthorList = useMemo(
    () => buildIssueCommentsAuthorList(mergedConversationComments),
    [mergedConversationComments],
  )

  const isCommentPanelFooterEnabled = !isReviewCommentsViewMode(viewMode) && currentUserCanComment
  const commentPanelFooter = isCommentPanelFooterEnabled ? (
    <Dialog.Footer>
      <CommentsSidePanelFooter
        ref={footerRef}
        commentText={commentText}
        onCommentTextChange={handleCommentTextChange}
        onSubmitComment={handleSubmitComment}
        isPending={isSubmittingComment}
      />
    </Dialog.Footer>
  ) : null

  const limitExceeded = pageLimits?.reviewThreadsPaginatedLimitExceeded
  const limitCount = pageLimits?.reviewThreadsPaginatedLimit

  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')
  const shouldLazyLoadData = isOpen && !isFilesChanged && updatedPullRequestHeader
  const layoutStoreSeeded = useLayoutStoreSeeded()
  const [lazyDataLoaded, setLazyDataLoaded] = useState(false)
  const handleLazyDataLoaded = useCallback(() => setLazyDataLoaded(true), [])
  const isLazyLoading = shouldLazyLoadData && !lazyDataLoaded && !layoutStoreSeeded

  // Subscribe to alive events for live comment updates from other users.
  // This must be above the `if (!isOpen)` guard so the subscription stays
  // active even when the panel is closed. The hook self-activates only
  // once comments have been loaded into the store.
  useIssueCommentLiveUpdates(aliveChannels)

  const isCompact = useIsDockedPanelCompact()

  if (!isOpen) return null

  if (sidePanelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.Comments}>
        <Panel.Header>
          {/* TODO: Update docked panel to allow setting a react child instead of "Panel Name" which would allow
          adding the button as the panel header, similar to how the mobile dialog works below */}
          {isThreadDetailMode ? (
            <div className="py-2">
              <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
                Back to all comments
              </Button>
            </div>
          ) : (
            <CommentPanelHeaderContent
              threadPreviewsAuthorList={threadPreviewsAuthorList}
              issueCommentsAuthorList={issueCommentsAuthorList}
              threadPreviewFilterState={threadPreviewFilterState}
              setThreadPreviewFilterState={setThreadPreviewFilterState}
              issueCommentsFilterState={issueCommentsFilterState}
              setIssueCommentsFilterState={setIssueCommentsFilterState}
              iconOnlyReviewCommentsFilter
            />
          )}
        </Panel.Header>
        <Panel.Body>
          {shouldLazyLoadData && (
            <ErrorBoundary boundaryName="LazyDataLoader" fallback={null}>
              <Suspense fallback={null}>
                <LazyDataLoader onLoaded={handleLazyDataLoaded} />
              </Suspense>
            </ErrorBoundary>
          )}
          <CommentsPanelBody
            filteredThreadIds={filteredThreadIds}
            filteredIssueCommentIds={filteredIssueCommentIds}
            conversationItems={conversationItems}
            handleQuoteReply={handleQuoteReply}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            threadPreviews={threadPreviews}
            totalDiffCommentCount={totalDiffCommentCount}
            limitCount={limitCount}
            isOpen={isOpen}
            isLazyLoading={isLazyLoading}
            onClose={noop}
            tabSize={tabSize}
          />
        </Panel.Body>
        {isCommentPanelFooterEnabled && (
          <Panel.Footer>
            <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
              <CommentsSidePanelFooter
                ref={footerRef}
                commentText={commentText}
                onCommentTextChange={handleCommentTextChange}
                onSubmitComment={handleSubmitComment}
                isPending={isSubmittingComment}
              />
            </FeatureFlags>
          </Panel.Footer>
        )}
      </Panel>,
      sidePanelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderFooter={() => {
        return commentPanelFooter
      }}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            {isThreadDetailMode ? (
              <>
                <span className="sr-only" id={dialogLabelId}>
                  Comments
                </span>
                <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
                  Back to all comments
                </Button>
              </>
            ) : (
              <Dialog.Title id={dialogLabelId}>Comments</Dialog.Title>
            )}
            <IconButton
              ref={closeButtonRef}
              aria-label="Close comments panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
          {!isThreadDetailMode ? (
            <CommentPanelHeaderContent
              threadPreviewsAuthorList={threadPreviewsAuthorList}
              issueCommentsAuthorList={issueCommentsAuthorList}
              threadPreviewFilterState={threadPreviewFilterState}
              setThreadPreviewFilterState={setThreadPreviewFilterState}
              issueCommentsFilterState={issueCommentsFilterState}
              setIssueCommentsFilterState={setIssueCommentsFilterState}
            />
          ) : null}
        </Dialog.Header>
      )}
    >
      {shouldLazyLoadData && (
        <ErrorBoundary boundaryName="LazyDataLoader" fallback={null}>
          <Suspense fallback={null}>
            <LazyDataLoader onLoaded={handleLazyDataLoaded} />
          </Suspense>
        </ErrorBoundary>
      )}
      <CommentsPanelBody
        limitExceeded={limitExceeded}
        limitCount={limitCount}
        isOpen={isOpen}
        isLazyLoading={isLazyLoading}
        conversationItems={conversationItems}
        onClose={onClose}
        tabSize={tabSize}
        filteredThreadIds={filteredThreadIds}
        filteredIssueCommentIds={filteredIssueCommentIds}
        handleQuoteReply={handleQuoteReply}
        pathName={pathName}
        threadPreviews={threadPreviews}
        totalDiffCommentCount={totalDiffCommentCount}
        pullRequestAuthorLogin={pullRequestAuthorLogin}
      />
    </Dialog>
  )
})
